/**
 * Configuration Language Parser Implementation
 *
 * Implements the Configuration Language specification from https://deanebarker.net/tech/config-lang/
 * Provides classes for parsing command-based configuration syntax.
 */

class Argument {
  /**
   * Creates an Argument instance
   * @param {string} key - The argument key/name
   * @param {string} value - The argument value
   */
  constructor(key, value = "true") {
    this.key = key;
    this.value = value;
  }

  /**
   * Parses an argument from source string
   * @param {string} source - Source string like "-key:value" or "-key"
   * @returns {Argument} Parsed argument
   */
  static fromSource(source) {
    if (!source.startsWith("-")) {
      throw new Error(`Invalid argument format: ${source}`);
    }

    const withoutDash = source.substring(1);
    const colonIndex = withoutDash.indexOf(":");

    if (colonIndex === -1) {
      // No colon means boolean argument with default value "true"
      const key = withoutDash.trim();
      this.validateName(key);
      return new Argument(key, "true");
    }

    const key = withoutDash.substring(0, colonIndex);
    const value = withoutDash.substring(colonIndex + 1);

    this.validateName(key);
    return new Argument(key, value);
  }

  /**
   * Validates argument name follows naming rules
   * @param {string} name - Name to validate
   */
  static validateName(name) {
    if (!name || name.length === 0) {
      throw new Error("Argument name cannot be empty");
    }

    if (!/^[a-zA-Z][a-zA-Z0-9_.-]*$/.test(name)) {
      throw new Error(
        `Invalid argument name: ${name}. Must start with letter and contain only letters, digits, underscore, dash, or period.`
      );
    }
  }
}

class Command {
  /**
   * Creates a Command instance
   * @param {string} name - The command name
   * @param {Argument[]} args - Array of arguments
   * @param {string} target - Optional target name
   */
  constructor(name, args = [], target = null) {
    Command.validateName(name);
    this.name = name;
    this.arguments = args || [];
    this.target = target;
  }

  /**
   * Parses a command from source string
   * @param {string} source - Source string like "command -arg1:value1 -arg2:value2" or "command -arg1:value1 => target"
   * @param {Object} tokenMap - Map of token names to values for substitution
   * @returns {Command} Parsed command
   */
  static fromSource(source, tokenMap = {}) {
    const trimmed = source.trim();
    if (!trimmed) {
      throw new Error("Command source cannot be empty");
    }

    // Extract target if present (=> syntax)
    let target = null;
    let sourceWithoutTarget = trimmed;

    const arrowIndex = trimmed.indexOf("=>");
    if (arrowIndex !== -1) {
      const targetPart = trimmed.substring(arrowIndex + 2).trim();
      if (targetPart) {
        target = targetPart;
        Command.validateName(target);
        sourceWithoutTarget = trimmed.substring(0, arrowIndex).trim();
      }
    }

    const tokens = this.tokenize(sourceWithoutTarget);
    const commandName = tokens[0].value;

    Command.validateName(commandName);

    const args = [];
    const argKeys = new Set();

    for (let i = 1; i < tokens.length; i++) {
      const token = tokens[i];
      if (token.value.startsWith("-")) {
        const arg = Argument.fromSource(token.value);

        // Check for duplicate argument keys
        if (argKeys.has(arg.key)) {
          throw new Error(`Duplicate argument key: ${arg.key}`);
        }
        argKeys.add(arg.key);

        // Perform token substitution only for unquoted values
        if (arg.value.startsWith("$") && !token.wasQuoted) {
          const varName = arg.value.substring(1);
          if (tokenMap.hasOwnProperty(varName)) {
            arg.value = tokenMap[varName];
          } else {
            throw new Error(`Undefined token: ${varName}`);
          }
        }

        args.push(arg);
      }
    }

    return new Command(commandName, args, target);
  }

  /**
   * Tokenizes a command line respecting quotes
   * @param {string} line - Line to tokenize
   * @returns {Array} Array of token objects with value and quoted flag
   */
  static tokenize(line) {
    const tokens = [];
    let current = "";
    let inQuotes = false;
    let quoteChar = "";
    let escaped = false;
    let currentWasQuoted = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (escaped) {
        current += char;
        escaped = false;
        continue;
      }

      if (char === "\\" && inQuotes) {
        escaped = true;
        continue;
      }

      if ((char === '"' || char === "'") && !inQuotes) {
        inQuotes = true;
        quoteChar = char;
        currentWasQuoted = true;
        continue;
      }

      if (char === quoteChar && inQuotes) {
        inQuotes = false;
        quoteChar = "";
        continue;
      }

      if (!inQuotes && /\s/.test(char)) {
        if (current.length > 0) {
          tokens.push({ value: current, wasQuoted: currentWasQuoted });
          current = "";
          currentWasQuoted = false;
        }
        continue;
      }

      current += char;
    }

    if (current.length > 0) {
      tokens.push({ value: current, wasQuoted: currentWasQuoted });
    }

    if (inQuotes) {
      throw new Error("Unclosed quote in command");
    }

    return tokens;
  }

  /**
   * Validates command name follows naming rules
   * @param {string} name - Name to validate
   */
  static validateName(name) {
    if (!name || name.length === 0) {
      throw new Error("Command name cannot be empty");
    }

    if (!/^[a-zA-Z][a-zA-Z0-9_.-]*$/.test(name)) {
      throw new Error(
        `Invalid command name: ${name}. Must start with letter and contain only letters, digits, underscore, dash, or period.`
      );
    }
  }
}

class CommandSet {
  /**
   * Creates a CommandSet instance
   * @param {string} source - Optional source to parse immediately
   */
  constructor(source = "") {
    this.commands = [];
    this.tokens = {};
    this._listeners = {};

    if (source) {
      this.parse(source);
    }
  }

  /**
   * Registers a handler for a named event.
   * @param {string} eventName - Event name (e.g. "config-lang:before-line-parsed")
   * @param {(event: {type: string, detail: object}) => void} handler - Receives an event with a mutable `detail`
   */
  addEventListener(eventName, handler) {
    if (typeof handler !== "function") {
      throw new Error("Event handler must be a function");
    }
    if (!this._listeners[eventName]) {
      this._listeners[eventName] = [];
    }
    this._listeners[eventName].push(handler);
  }

  /**
   * Removes a previously-registered handler.
   */
  removeEventListener(eventName, handler) {
    const handlers = this._listeners[eventName];
    if (!handlers) return;
    this._listeners[eventName] = handlers.filter((h) => h !== handler);
  }

  /**
   * Dispatches an event to all registered handlers. Handlers may mutate
   * `detail` to influence subsequent parsing.
   * @returns {{type: string, detail: object}} The event object after handlers ran
   */
  _dispatchEvent(eventName, detail) {
    const event = { type: eventName, detail };
    const handlers = this._listeners[eventName];
    if (handlers) {
      for (const handler of handlers) {
        handler(event);
      }
    }
    return event;
  }

  /**
   * Parses configuration language source
   * @param {string} source - Source text to parse
   */
  parse(source) {
    if (typeof source !== 'string') {
      throw new Error('Source must be a string');
    }
    
    this.commands = [];
    this.tokens = {};

    const rawLines = source.split("\n");

    // Fire the before-line-parsed event for every raw line so handlers can
    // transform any line — including token value lines — before parsing.
    const lines = rawLines.map((line) => {
      const event = this._dispatchEvent("config-lang:before-line-parsed", { line });
      return event.detail.line;
    });

    // Find the token-section boundary in the raw lines. A token definition
    // is a non-indented line starting with "$". We split BEFORE preprocessing
    // so that comment-stripping and indentation-joining only apply to the
    // commands section — token values may legitimately contain "#" lines,
    // blank lines, and leading whitespace.
    let tokenStartIndex = -1;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.length > 0 && !/^\s/.test(line) && line.startsWith("$")) {
        tokenStartIndex = i;
        break;
      }
    }

    let commandLines, tokenLines;
    if (tokenStartIndex === -1) {
      commandLines = this.preprocessLines(lines);
      tokenLines = [];
    } else {
      commandLines = this.preprocessLines(lines.slice(0, tokenStartIndex));
      tokenLines = lines.slice(tokenStartIndex);
    }

    // Parse tokens first (needed for substitution)
    this.parseTokens(tokenLines);

    // Parse commands
    this.parseCommands(commandLines);

    // Clear tokens after parsing since they're no longer needed
    // Token substitution occurs at parse time, not runtime
    this.tokens = {};
  }

  /**
   * Preprocesses lines to handle indentation and comments
   * @param {string[]} lines - Raw lines from source
   * @returns {string[]} Processed lines
   */
  preprocessLines(lines) {
    const result = [];
    let currentLine = "";

    for (let line of lines) {
      // Skip comments and blank lines
      if (line.trim().startsWith("#") || line.trim() === "") {
        continue;
      }

      // Check if line is indented
      if (/^\s/.test(line)) {
        // Indented line - append to current line
        currentLine += " " + line.trim();
      } else {
        // Non-indented line
        if (currentLine) {
          result.push(currentLine);
        }
        currentLine = line;
      }
    }

    if (currentLine) {
      result.push(currentLine);
    }

    return result;
  }

  /**
   * Parses command lines
   * @param {string[]} lines - Command lines to parse
   */
  parseCommands(lines) {
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed) {
        try {
          const command = Command.fromSource(trimmed, this.tokens);
          this.commands.push(command);
        } catch (error) {
          throw new Error(
            `Error parsing command "${trimmed}": ${error.message}`
          );
        }
      }
    }
  }

  /**
   * Parses token definitions
   * @param {string[]} lines - Token lines to parse
   */
  parseTokens(lines) {
    let currentToken = null;
    let currentValue = [];

    for (const line of lines) {
      // Only a non-indented line starting with "$" begins a new token.
      // Indented "$..." lines are treated as part of the previous token's value.
      if (line.length > 0 && !/^\s/.test(line) && line.startsWith("$")) {
        // Save previous token if exists
        if (currentToken) {
          this.tokens[currentToken] = currentValue.join("\n").trim();
        }

        // Start new token
        currentToken = line.substring(1).trim();
        CommandSet.validateTokenName(currentToken);
        currentValue = [];
      } else {
        // Token value line
        if (currentToken) {
          currentValue.push(line);
        }
      }
    }

    // Save last token
    if (currentToken) {
      this.tokens[currentToken] = currentValue.join("\n").trim();
    }
  }

  /**
   * Validates token name follows naming rules
   * @param {string} name - Name to validate
   */
  static validateTokenName(name) {
    if (!name || name.length === 0) {
      throw new Error("Token name cannot be empty");
    }

    if (!/^[a-zA-Z][a-zA-Z0-9_.-]*$/.test(name)) {
      throw new Error(
        `Invalid token name: ${name}. Must start with letter and contain only letters, digits, underscore, dash, or period.`
      );
    }
  }

  /**
   * Gets all commands with a specific name
   * @param {string} name - Command name to filter by
   * @returns {Command[]} Array of matching commands
   */
  getCommands(name) {
    return this.commands.filter((cmd) => cmd.name === name);
  }

  /**
   * Gets the first command with a specific name
   * @param {string} name - Command name to find
   * @returns {Command|null} First matching command or null
   */
  getCommand(name) {
    return this.commands.find((cmd) => cmd.name === name) || null;
  }

  /**
   * Adds a command to the command set
   * @param {Command} command - Command to add
   */
  addCommand(command) {
    if (!(command instanceof Command)) {
      throw new Error("Must provide a Command instance");
    }
    this.commands.push(command);
  }
}

/**
 * Extracts pragma tokens from the top of a source string.
 *
 * Pragma lines must appear at the top of the input (blank lines allowed
 * between them) and follow the form:
 *   #<optional whitespace>pragma<whitespace><comma- or space-delimited tokens>
 * The keyword "pragma" is case-insensitive. Returned tokens are lower-cased.
 * Searching stops at the first non-blank line that is not a pragma line.
 *
 * @param {string} source - Multi-line string to scan
 * @returns {string[]} Array of lower-cased pragma tokens
 */
function extractPragmas(source) {
  if (typeof source !== "string") {
    throw new Error("Source must be a string");
  }

  const pragmas = [];
  const pragmaRegex = /^\s*#\s*pragma\s+(.+)$/i;

  for (const line of source.split("\n")) {
    if (line.trim() === "") {
      continue;
    }

    const match = line.match(pragmaRegex);
    if (!match) {
      break;
    }

    const tokens = match[1].split(/[,\s]+/).filter((t) => t.length > 0);
    for (const token of tokens) {
      pragmas.push(token.toLowerCase());
    }
  }

  return pragmas;
}

export { CommandSet, Command, Argument, extractPragmas };